import React, { type FC, type ReactNode, type PropsWithChildren } from 'react';

import Box from '../../box/box';
import ErrorMessage from '../error-message/error-message';
import Label from '../label/label';
import type { Lucid } from '../../../types/types';

export type FieldWrapperProps = {
  /** The Field component passed in as children. */
  children?: ReactNode;
  /** Allows the component to get additional styles. */
  className?: string;
  /** A specified message to be shown under the form input. */
  customErrorMessage?: string;
  /** Checks if the field has an error message. */
  hasError?: boolean;
  /** Determines whether the text should be light or dark */
  hasDarkBackground?: boolean;
  /** Checks if the checkbox field has instructions text. */
  hasInstructions?: boolean;
  /** The id that links the form input with the label. */
  id: string;
  /** Checks if the component is a checkbox. */
  isCheckbox?: boolean;
  /** Checks if the component is a checkbox group. */
  isCheckboxGroup?: boolean;
  /** Determines whether to use Dark Mode styles */
  isDarkMode?: boolean;
  /** Checks if the field is disabled. */
  isDisabled?: boolean;
  /** Checks if the component is a dropdown. */
  isDropdown?: boolean;
  /** Checks if the field should be full width. */
  isFullWidth?: boolean;
  /** Checks if the field is hidden. */
  isHidden?: boolean;
  /** Checks if the component is a radio button. */
  isRadioButton?: boolean;
  /** Checks if the field is required to submit and displays an asterick. */
  isRequired?: boolean;
  /** The label for the form component. */
  label?: string;
  /** Adds the id to the label component. */
  labelId?: string;
  /** The variant for the Label component. */
  labelVariant?: Lucid.Theme.Component.Typography.VariantKeys;
  /** Adds a specific role to the Field Wrapper element. */
  role?: string;
};

const FieldWrapper: FC<PropsWithChildren<FieldWrapperProps>> = ({
  children,
  customErrorMessage,
  className,
  hasError = false,
  hasDarkBackground,
  hasInstructions = false,
  id,
  isCheckbox = false,
  isCheckboxGroup = false,
  isDarkMode = false,
  isDisabled = false,
  isDropdown = false,
  isFullWidth = false,
  isHidden = false,
  isRadioButton = false,
  isRequired = false,
  label,
  labelId,
  labelVariant,
  ...rest
}) => (
  <Box
    {...rest}
    css={theme => [
      {
        label: 'fieldWrapper',
        flex: '0 1 100%',
        maxWidth: '100%',
        marginBottom: theme.space.s16,
        [theme.breakpoints.bp900.min]: {
          flex: '0 1 49%',
          maxWidth: '49%',
        },
      },
      isFullWidth && {
        [theme.breakpoints.bp900.min]: {
          flex: '0 1 100%',
          maxWidth: '100%',
        },
      },
      hasError && {
        marginBottom: '12px',
      },
      isCheckbox && [
        {
          display: 'flex',
          alignItems: 'center',
          '& > label': {
            fontWeight: theme.fontWeights.fw700,
          },
        },
        hasError && {
          flexWrap: 'wrap',
          '& > label, & > input': {
            alignSelf: 'flex-end',
          },
          '& > span': {
            alignSelf: 'flex-start',
            paddingTop: '10px',
          },
        },
        hasInstructions && {
          flexWrap: 'wrap',
          padding: theme.space.s24,
          borderRadius: theme.radii.r4,
          '& > p': {
            paddingTop: theme.space.s24,
          },
          '&:hover': {
            backgroundColor: theme.colors.coolGrey[5],
          },
        },
      ],
      isDropdown && {
        '& > div': {
          marginTop: '8px',
        },
      },
      isRadioButton && {
        display: 'flex',
        '& > span': {
          paddingTop: '10px',
        },
      },
      isHidden && {
        display: 'none',
        visibility: 'hidden',
      },
    ]}
    // eslint-disable-next-line @stylistic/jsx-sort-props
    className={className}
    data-testid={`formFieldWrapper-${id}`}
  >
    <Label
      css={theme => [
        {
          fontSize: theme.fontSizes.fs12,
        },
        isCheckbox && {
          order: 2,
          marginBottom: 0,
        },
        isCheckboxGroup && {
          order: 0,
          marginBottom: theme.space.s16,
        },
        isRadioButton && {
          order: 0,
          marginBottom: theme.space.s16,
        },
      ]}
      hasDarkBackground={hasDarkBackground}
      htmlFor={isDropdown ? undefined : id}
      id={labelId}
      isDarkMode={isDarkMode}
      isDisabled={isDisabled}
      isHidden={isHidden}
      isRequired={isRequired}
      label={label}
      variant={labelVariant}
    />
    {children}
    {hasError && (
      <ErrorMessage
        customErrorMessage={customErrorMessage}
        hasDarkBackground={hasDarkBackground}
        id={id}
        isCheckbox={isCheckbox}
        isDarkMode={isDarkMode}
        isDisabled={isDisabled}
      />
    )}
  </Box>
);

export { FieldWrapper };
export default FieldWrapper;
